import { useState } from "react";

interface InputProps<K extends string, V extends string> {
  label: string;
  labelClassName?: string;
  name: K;
  value?: V;
  placeholder: string;
  inputStyle?: React.CSSProperties;
  onChange: (name: K, value: V) => void;
}

export const InlineInput = <K extends string>({
  label,
  labelClassName,
  name,
  value = "",
  placeholder,
  inputStyle = {},
  onChange,
}: InputProps<K, string>) => {
  const [error, setError] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Clear error when input is empty
    if (!newValue) {
      setError("");
      onChange(name, newValue);
      return;
    }

    // Validate if input is a number
    const numValue = parseInt(newValue);
    if (isNaN(numValue)) {
      setError("Please enter a number");
      return;
    }

    // Check if font size exceeds 15pt
    if (numValue > 15) {
      setError("Maximum 20pt allowed");
      return;
    }

    // Clear error and update value if valid
    setError("");
    onChange(name, newValue);
  };

  return (
    <div className="flex flex-col gap-1">
      <label
        className={`flex gap-2 text-base font-medium text-gray-700 ${labelClassName}`}
      >
        <span className="w-28">{label}</span>
        <input
          type="text"
          name={name}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          className="w-[5rem] border-b border-gray-300 text-center font-semibold leading-3 outline-none"
          style={inputStyle}
        />
      </label>
      {error && <span className="ml-30 text-sm text-red-500">{error}</span>}
    </div>
  );
};
